import { useState, useMemo } from 'react'
import { where, orderBy } from 'firebase/firestore'
import { format, subDays, startOfDay } from 'date-fns'
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { useCollection } from '@/hooks/useFirestore'
import { useBusinesses, useUsers } from '@/hooks/useAdminData'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useTranslation } from 'react-i18next'
import { enumLabel } from '@/lib/enumLabel'
import type { UsageAnalytics, UsageEvent } from '@/types'
import { Download, Activity, Phone, MessageSquare, Zap, Info } from 'lucide-react'
import { Timestamp } from 'firebase/firestore'

type DateRange = '7' | '30' | '90'

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

export function AnalyticsPage() {
  const { t } = useTranslation()
  const [dateRange, setDateRange] = useState<DateRange>('30')

  const startDate = useMemo(() => {
    const days = Number(dateRange)
    return startOfDay(subDays(new Date(), days))
  }, [dateRange])

  const startDateStr = format(startDate, 'yyyy-MM-dd')

  const { data: analytics, isLoading: analyticsLoading } = useCollection<UsageAnalytics>(
    'usage_analytics',
    [where('date', '>=', startDateStr), orderBy('date', 'asc')]
  )

  const { data: recentEvents } = useCollection<UsageEvent>(
    'usage_events',
    [where('timestamp', '>', Timestamp.fromDate(startDate)), orderBy('timestamp', 'desc')]
  )

  const { data: users } = useUsers()
  const { data: businesses } = useBusinesses()

  const metrics = useMemo(() => {
    if (!analytics?.length) return { activeUsers: 0, totalCalls: 0, totalMessages: 0, featuresUsed: 0 }
    const latest = analytics[analytics.length - 1]
    const totalCalls = analytics.reduce((s, a) => s + (a.total_calls ?? 0), 0)
    const totalMessages = analytics.reduce((s, a) => s + (a.total_messages ?? 0), 0)
    const featureUsage = analytics.reduce((acc, a) => {
      Object.entries(a.feature_usage || {}).forEach(([k, v]) => {
        acc[k] = (acc[k] || 0) + v
      })
      return acc
    }, {} as Record<string, number>)
    return {
      activeUsers: latest?.active_users ?? 0,
      totalCalls,
      totalMessages,
      featuresUsed: Object.keys(featureUsage).length,
    }
  }, [analytics])

  const lineChartData = useMemo(() => {
    return (analytics ?? []).map((a) => ({
      date: a.date,
      activeUsers: a.active_users ?? 0,
      totalUsers: a.total_users ?? 0,
    }))
  }, [analytics])

  const featureBarData = useMemo(() => {
    const counts: Record<string, number> = {}
    recentEvents
      ?.filter((e) => e.event_type === 'feature_used' && e.feature_name)
      .forEach((e) => {
        counts[e.feature_name!] = (counts[e.feature_name!] || 0) + 1
      })
    return Object.entries(counts)
      .map(([name, count]) => ({ name: enumLabel(t, 'featureNames', name), count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
  }, [recentEvents, t])

  // Subscriptions belong to the care circle (businesses/{id}.subscription.tier), not
  // to individual users. With no circle on a paid tier there is nothing to chart,
  // so the panel says so instead of drawing a single "None" slice.
  const tierDistribution = useMemo(() => {
    const counts: Record<string, number> = {}
    businesses?.forEach((b) => {
      const tier = b.subscription?.tier || 'none'
      counts[tier] = (counts[tier] || 0) + 1
    })
    if (Object.keys(counts).every((tier) => tier === 'none')) return []
    return Object.entries(counts).map(([name, value]) => ({ name: enumLabel(t, 'tiers', name), value }))
  }, [businesses, t])

  const activeUsersList = useMemo(() => {
    if (!recentEvents || !users) return []
    const oneDayAgo = subDays(new Date(), 1)
    const recentUserIds = new Set(
      recentEvents
        .filter((e) => e.timestamp?.toDate?.() > oneDayAgo)
        .map((e) => e.user_id)
    )
    return users.filter((u) => recentUserIds.has(u.id)).slice(0, 20)
  }, [recentEvents, users])

  const hasAnalytics = (analytics?.length ?? 0) > 0

  const exportCSV = () => {
    if (!analytics?.length) return
    // Column names stay English: the export is a machine-readable file, not UI copy.
    const headers = ['Date', 'Active Users', 'Total Users', 'Calls', 'Messages']
    const rows = analytics.map((a) =>
      [a.date, a.active_users, a.total_users, a.total_calls, a.total_messages].join(',')
    )
    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `analytics_${startDateStr}_${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const metricCards = [
    { label: t('analytics.activeUsers'), value: metrics.activeUsers, icon: Activity, color: 'text-blue-600 bg-blue-100' },
    { label: t('analytics.totalCalls'), value: metrics.totalCalls, icon: Phone, color: 'text-green-600 bg-green-100' },
    { label: t('analytics.totalMessages'), value: metrics.totalMessages, icon: MessageSquare, color: 'text-purple-600 bg-purple-100' },
    { label: t('analytics.featuresUsed'), value: metrics.featuresUsed, icon: Zap, color: 'text-amber-600 bg-amber-100' },
  ]

  if (analyticsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2">
          {(['7', '30', '90'] as const).map((range) => (
            <Button
              key={range}
              variant={dateRange === range ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setDateRange(range)}
            >
              {t('analytics.rangeDays', { count: Number(range) })}
            </Button>
          ))}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={exportCSV}
          disabled={!hasAnalytics}
          title={hasAnalytics ? undefined : t('analytics.exportEmpty')}
        >
          <Download className="h-4 w-4" />
          {t('analytics.exportCsv')}
        </Button>
      </div>

      {!hasAnalytics && (recentEvents?.length ?? 0) === 0 && (
        <div className="flex gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          <Info className="h-5 w-5 shrink-0" />
          <p>{t('analytics.notRecorded')}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metricCards.map((card) => (
          <Card key={card.label}>
            <CardContent className="flex items-center gap-4">
              <div className={`rounded-lg p-3 ${card.color}`}>
                <card.icon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{card.label}</p>
                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <h3 className="font-semibold text-gray-900">{t('analytics.userActivity')}</h3>
          </CardHeader>
          <CardContent>
            {lineChartData.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">{t('analytics.noData')}</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={lineChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="activeUsers" stroke="#3b82f6" name={t('analytics.activeUsers')} />
                  <Line type="monotone" dataKey="totalUsers" stroke="#10b981" name={t('analytics.totalUsers')} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="font-semibold text-gray-900">{t('analytics.featureUsage')}</h3>
          </CardHeader>
          <CardContent>
            {featureBarData.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">{t('analytics.noFeatureData')}</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={featureBarData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name={t('analytics.usageCount')} fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <h3 className="font-semibold text-gray-900">{t('analytics.subscriptionDistribution')}</h3>
          </CardHeader>
          <CardContent>
            {tierDistribution.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">{t('analytics.noSubscriptionData')}</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={tierDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {tierDistribution.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="font-semibold text-gray-900">{t('analytics.activeUsers24h')}</h3>
          </CardHeader>
          <CardContent className="p-0">
            {activeUsersList.length === 0 ? (
              <p className="px-6 py-8 text-center text-sm text-gray-500">
                {t('analytics.noActiveUsers')}
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
                {activeUsersList.map((u) => (
                  <li key={u.id} className="flex items-center justify-between px-6 py-2.5">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {u.displayName}
                      </p>
                      <p className="text-xs text-gray-500">{u.email}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
